// React
import { useState, useEffect, useCallback } from 'react'

// Next
import { Link, useSearchParams } from 'react-router-dom'

// Components
import ScrollableScreen from '@/components/ui/ScrollableScreen'
import LocationSelect from '@/components/ui/LocationSelect'
import SKU from '@/components/ui/SKU'

// Hooks
import useUserLocation from '@/hooks/useUserLocation'
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Mantine
import { Button, Loader, Select } from '@mantine/core'

// Icons
import { IconEqual, IconMinus, IconPlus } from '@tabler/icons-react'

const coverageOptions = [
    { value: '1', label: '1 semaine' },
    { value: '2', label: '2 semaines' },
    { value: '3', label: '3 semaines' },
    { value: '4', label: '4 semaines' },
]

// Number formatter
const formatNumber = (value) => {
    if (value === null || value === undefined) return '—'
    return new Intl.NumberFormat('fr-FR').format(value)
}

// Get color class based on stock value
const getStockColorClass = (value) => {
    if (value === null || value === undefined) return 'text-gray-400'
    if (value === 0) return 'text-red-600'
    if (value < 5) return 'text-orange-500'
    return 'text-green-600'
}

const ComponentTable = ({
    data,
    emptyMessage,
    orderQuantities,
    onUpdateOrderQuantity,
    onSetOrderQuantity,
    onFinalizeOrderQuantity,
    calculateTargetStock,
}) => (
    <div className="bg-white border rounded-lg max-h-[60vh] overflow-auto max-w-4xl mx-auto">
        <table className="w-full table-fixed">
            <thead className="sticky top-0 z-10">
                <tr className="border-b">
                    <th className="text-left px-4 py-3 font-medium text-gray-700 bg-gray-50">
                        Composant
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-gray-700 w-28 bg-gray-50 whitespace-nowrap">
                        Dispo. MNP
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-gray-700 w-24 bg-gray-50 whitespace-nowrap">
                        Dispo. ici
                    </th>
                    <th className="text-center px-4 py-3 font-medium text-gray-700 w-36 bg-gray-50">
                        À commander
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-gray-700 w-20 bg-gray-50">
                        Besoin
                    </th>
                </tr>
            </thead>
            <tbody>
                {data.map((component) => {
                    const isTracked = component.is_tracked
                    const targetStock = isTracked ? calculateTargetStock(component) : null
                    const availableHere = component.available_at_location ?? 0
                    const orderQty = orderQuantities[component.id] || 0
                    const remainingNeed = targetStock !== null
                        ? targetStock - availableHere - orderQty
                        : null
                    const hasUnfilledNeed = remainingNeed !== null && remainingNeed > 0
                    const idealOrder = targetStock !== null
                        ? Math.max(0, targetStock - availableHere)
                        : 0
                    const maxOrder = component.available_at_mnp ?? 0
                    const fillValue = Math.min(idealOrder, maxOrder)

                    return (
                        <tr
                            key={component.id}
                            className={`border-b last:border-b-0 ${hasUnfilledNeed ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
                        >
                            <td className="px-4 py-3">
                                <div className="flex items-center gap-2 min-w-0">
                                    <SKU component={component} size="sm" />
                                    <Link
                                        to={`/components/${component.id}`}
                                        className="text-blue-600 hover:underline truncate"
                                        title={component.full_name}
                                    >
                                        {component.full_name}
                                    </Link>
                                </div>
                            </td>
                            <td className={`px-4 py-3 text-right tabular-nums ${getStockColorClass(component.available_at_mnp)}`}>
                                {formatNumber(component.available_at_mnp)}
                            </td>
                            <td className={`px-4 py-3 text-right tabular-nums ${getStockColorClass(component.available_at_location)}`}>
                                {formatNumber(component.available_at_location)}
                            </td>
                            <td className="px-4 py-2">
                                <div className="flex items-center justify-center gap-1">
                                    <button
                                        type="button"
                                        className="w-7 h-7 flex items-center justify-center border rounded-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                        onClick={() =>
                                            onUpdateOrderQuantity(
                                                component.id,
                                                -1,
                                                maxOrder
                                            )
                                        }
                                        disabled={
                                            !orderQuantities[component.id] ||
                                            orderQuantities[component.id] === 0
                                        }
                                    >
                                        <IconMinus size={14} />
                                    </button>
                                    <input
                                        type="text"
                                        className="w-10 h-7 text-center border rounded-sm text-sm tabular-nums"
                                        value={orderQuantities[component.id] ?? 0}
                                        onChange={(e) =>
                                            onSetOrderQuantity(
                                                component.id,
                                                e.target.value,
                                                maxOrder
                                            )
                                        }
                                        onBlur={() => onFinalizeOrderQuantity(component.id)}
                                    />
                                    <button
                                        type="button"
                                        className="w-7 h-7 flex items-center justify-center border rounded-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                        onClick={() =>
                                            onUpdateOrderQuantity(
                                                component.id,
                                                1,
                                                maxOrder
                                            )
                                        }
                                        disabled={
                                            (orderQuantities[component.id] || 0) >= maxOrder
                                        }
                                    >
                                        <IconPlus size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        className="w-7 h-7 flex items-center justify-center border rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        onClick={() =>
                                            onSetOrderQuantity(
                                                component.id,
                                                fillValue,
                                                maxOrder
                                            )
                                        }
                                        disabled={
                                            fillValue === 0 ||
                                            (orderQuantities[component.id] || 0) === fillValue
                                        }
                                        title="Remplir avec le besoin"
                                    >
                                        <IconEqual size={14} />
                                    </button>
                                </div>
                            </td>
                            <td className={`px-4 py-3 text-right tabular-nums ${remainingNeed === null ? 'text-gray-400' : remainingNeed <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatNumber(remainingNeed)}
                            </td>
                        </tr>
                    )
                })}
                {data.length === 0 && (
                    <tr>
                        <td
                            colSpan={5}
                            className="px-4 py-8 text-center text-gray-500"
                        >
                            {emptyMessage}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
)

export default function ReplenishmentRequestPage() {
    const [searchParams, setSearchParams] = useSearchParams()

    // User location and all locations
    const { userLocationId, locations } = useUserLocation()

    // Get location_id and coverage from URL
    const locationId = searchParams.get('location_id')
    const coverage = searchParams.get('coverage') ?? '2'

    // Redirect to URL with location_id if not present
    useEffect(() => {
        if (!locationId && userLocationId) {
            setSearchParams({ location_id: userLocationId, coverage }, { replace: true })
        }
    }, [locationId, userLocationId])

    // Check if selected location can fulfill orders
    const selectedLocation = locations?.find(
        (loc) => String(loc.id) === locationId
    )
    const canFulfillOrders = selectedLocation?.can_fulfill_orders === true

    // Update URL when location changes
    const handleLocationChange = (value) => {
        const newParams = new URLSearchParams(searchParams)
        newParams.set('location_id', value)
        setSearchParams(newParams)
    }

    // Update URL when coverage changes
    const handleCoverageChange = (value) => {
        const newParams = new URLSearchParams(searchParams)
        newParams.set('coverage', value)
        setSearchParams(newParams)
    }

    // Data
    const [components, setComponents] = useState([])
    const [loading, setLoading] = useState(false)

    // Order quantities
    const [orderQuantities, setOrderQuantities] = useState({})

    // API
    const { requestGet } = useAxiosApi()

    // Load data when location changes
    useEffect(() => {
        if (!locationId || canFulfillOrders) {
            setComponents([])
            return
        }

        const loadData = async () => {
            setLoading(true)
            const response = await requestGet('/components/replenishment-needs', {
                location_id: locationId,
            })
            setComponents(response?.data?.data || [])
            setLoading(false)
        }

        loadData()
    }, [locationId, canFulfillOrders])

    // Calculate target stock based on flow rate (10% of global flow rate) and coverage weeks
    const calculateTargetStock = useCallback((component) => {
        if (!component.flow_rate) return null
        const coverageWeeks = parseInt(coverage, 10)
        const localFlowRate = component.flow_rate * 0.1
        return Math.ceil(localFlowRate * coverageWeeks * 7)
    }, [coverage])

    // Update order quantity for a component
    const updateOrderQuantity = (componentId, delta, max) => {
        setOrderQuantities((prev) => {
            const current = prev[componentId] || 0
            const newValue = Math.max(0, Math.min(max, current + delta))
            return { ...prev, [componentId]: newValue }
        })
    }

    const setOrderQuantity = (componentId, value, max) => {
        // Allow empty string for typing
        if (value === '') {
            setOrderQuantities((prev) => ({ ...prev, [componentId]: '' }))
            return
        }
        const parsed = parseInt(value, 10)
        if (isNaN(parsed)) {
            return
        }
        setOrderQuantities((prev) => ({
            ...prev,
            [componentId]: Math.max(0, Math.min(max, parsed)),
        }))
    }

    const finalizeOrderQuantity = (componentId) => {
        setOrderQuantities((prev) => {
            const current = prev[componentId]
            if (current === '' || current === undefined) {
                return { ...prev, [componentId]: 0 }
            }
            return prev
        })
    }

    const rightSection = (
        <div className="flex items-center gap-3">
            <Select
                size="sm"
                data={coverageOptions}
                value={coverage}
                onChange={handleCoverageChange}
                allowDeselect={false}
                w={140}
            />
            <LocationSelect
                size="sm"
                value={locationId}
                onChange={handleLocationChange}
            />
        </div>
    )

    return (
        <ScrollableScreen
            breadcrumbs={[
                { label: 'Stocks', href: '/components' },
                { label: 'Demande de réassort' },
            ]}
            rightSection={rightSection}
        >
            <div className="p-5 flex flex-col gap-5">
                {!locationId || loading ? (
                    <div className="flex justify-center py-10">
                        <Loader type="bars" size="sm" />
                    </div>
                ) : canFulfillOrders ? (
                    <div className="bg-white border rounded-lg p-8 text-center text-gray-500">
                        Cet emplacement peut préparer des commandes, il n'a pas
                        besoin de réassort.
                    </div>
                ) : (
                    <>
                        <ComponentTable
                            data={components}
                            emptyMessage="Aucun composant stocké à cet emplacement"
                            orderQuantities={orderQuantities}
                            onUpdateOrderQuantity={updateOrderQuantity}
                            onSetOrderQuantity={setOrderQuantity}
                            onFinalizeOrderQuantity={finalizeOrderQuantity}
                            calculateTargetStock={calculateTargetStock}
                        />
                        <div className="max-w-4xl mx-auto flex justify-end">
                            <Button disabled>
                                Créer la demande de réassort
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </ScrollableScreen>
    )
}
